/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * VPS Index Status Service
 * 
 * API client for VPS Context Library index status and settings endpoints.
 * Provides functions for checking index status, updating scheduler settings,
 * and triggering manual rebuilds.
 * 
 * @see server/routes/vps/index.ts
 */

import { authenticatedFetch, createQueryFn } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import type {
  VpsIndexStatusResponseData,
  UpdateIndexSettingsResponseData,
  TriggerIndexRebuildResponseData,
  IndexStatusResponseData,
} from '@shared/types/responses/vps-index';
import type { IndexType } from '@shared/types/requests/vps-index';

// ============================================================================
// API Endpoints - Centralized endpoint definitions
// ============================================================================

export const VPS_INDEX_ENDPOINTS = {
  status: '/api/vps/index/status',
  statusByType: (type: IndexType) => `/api/vps/index/status/${type}`,
  settings: '/api/vps/index/settings',
  rebuild: '/api/vps/index/rebuild',
} as const;

// ============================================================================
// Query Keys - For React Query cache management
// ============================================================================

export const VPS_INDEX_QUERY_KEYS = {
  all: ['vps-index'] as const,
  status: ['vps-index', 'status'] as const,
  statusByType: (type: IndexType) => ['vps-index', 'status', type] as const,
} as const;

// ============================================================================
// Re-export types for convenience
// ============================================================================

export type {
  VpsIndexStatusResponseData,
  UpdateIndexSettingsResponseData,
  TriggerIndexRebuildResponseData,
  IndexStatusResponseData,
  IndexType,
};

// ============================================================================
// Query Functions - For React Query
// ============================================================================

/**
 * Query function for fetching all index statuses
 * Usage:
 *   const { data } = useQuery({
 *     queryKey: VPS_INDEX_QUERY_KEYS.status,
 *     queryFn: vpsIndexStatusQueryFn,
 *   });
 */
export const vpsIndexStatusQueryFn = createQueryFn<VpsIndexStatusResponseData>(
  VPS_INDEX_ENDPOINTS.status
);

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get all index statuses and scheduler status
 */
export async function getVpsIndexStatus(): Promise<VpsIndexStatusResponseData> {
  const response = await authenticatedFetch(VPS_INDEX_ENDPOINTS.status);
  return handleApiResponse<VpsIndexStatusResponseData>(response);
}

/**
 * Get status for a specific index type
 */
export async function getIndexStatusByType(type: IndexType): Promise<IndexStatusResponseData> {
  const response = await authenticatedFetch(VPS_INDEX_ENDPOINTS.statusByType(type));
  return handleApiResponse<IndexStatusResponseData>(response);
}

/**
 * Update auto-rebuild settings
 */
export async function updateIndexSettings(settings: {
  autoRebuildEnabled?: boolean;
  rebuildIntervalMinutes?: number;
}): Promise<UpdateIndexSettingsResponseData> {
  const response = await authenticatedFetch(VPS_INDEX_ENDPOINTS.settings, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(settings),
  });
  return handleApiResponse<UpdateIndexSettingsResponseData>(response);
}

/**
 * Trigger manual index rebuild
 * @param types Optional array of index types to rebuild (defaults to all)
 */
export async function triggerIndexRebuild(types?: IndexType[]): Promise<TriggerIndexRebuildResponseData> {
  const response = await authenticatedFetch(VPS_INDEX_ENDPOINTS.rebuild, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ types }),
  });
  return handleApiResponse<TriggerIndexRebuildResponseData>(response);
}
